import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { OnboardingSubmission } from "../../types/onboarding";

// v5: aiSetupMethod no longer defaults to "openrouter" - the AI step makes
// the user pick a provider. Bumping the key discards v4 drafts, which all
// carry an aiSetupMethod the user never actively chose.
const DRAFT_KEY = "ztc.onboarding.draft.v5";

const detectTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const emptyDraft = (): OnboardingSubmission => ({
  timezone: detectTimezone(),
  agentName: "",
  // Telegram is preselected on the Channel step - it's the recommended
  // channel and included in every tier, so the default is always valid.
  preferredChannel: "telegram",
});

const readDraft = (): OnboardingSubmission => {
  if (typeof window === "undefined") return emptyDraft();
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return emptyDraft();
    const parsed = JSON.parse(raw) as Partial<OnboardingSubmission> | null;
    if (!parsed || typeof parsed !== "object") return emptyDraft();
    return { ...emptyDraft(), ...parsed };
  } catch {
    return emptyDraft();
  }
};

const writeDraft = (draft: OnboardingSubmission): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // localStorage failures (private mode, quota) shouldn't block the
    // wizard; in-memory state still drives the next/back UX.
  }
};

const clearDraft = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(DRAFT_KEY);
  } catch {
    // Same rationale as writeDraft: best-effort.
  }
};

export type OnboardingContextValue = {
  draft: OnboardingSubmission;
  update: <K extends keyof OnboardingSubmission>(key: K, value: OnboardingSubmission[K]) => void;
  reset: () => void;
};

const OnboardingContextRef = createContext<OnboardingContextValue | null>(null);

export const OnboardingProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [draft, setDraft] = useState<OnboardingSubmission>(() => readDraft());

  useEffect(() => {
    writeDraft(draft);
  }, [draft]);

  const update = useCallback(
    <K extends keyof OnboardingSubmission>(key: K, value: OnboardingSubmission[K]): void => {
      setDraft((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const reset = useCallback((): void => {
    clearDraft();
    setDraft(emptyDraft());
  }, []);

  const value = useMemo<OnboardingContextValue>(
    () => ({ draft, update, reset }),
    [draft, update, reset],
  );

  return <OnboardingContextRef.Provider value={value}>{children}</OnboardingContextRef.Provider>;
};

export const useOnboardingDraft = (): OnboardingContextValue => {
  const ctx = useContext(OnboardingContextRef);
  if (!ctx) {
    throw new Error("useOnboardingDraft must be used inside <OnboardingProvider>");
  }
  return ctx;
};
